import { AlertTriangle } from 'lucide-react'
import {
  Bar,
  BarChart,
  Cell,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import type { BrPipeline, PipelineStage } from '../../hooks/useBusinessReview'
import { formatKpiEur } from '../../lib/formatters'

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function fmtDays(v: number | null): string {
  return v != null ? `${Math.round(v)}gg` : '—'
}

function isAged(stage: PipelineStage, threshold: number): boolean {
  return stage.aging_median_days != null && stage.aging_median_days > threshold
}

function hasWeighted(stages: PipelineStage[]): boolean {
  return stages.some(s => s.weighted_value != null)
}

// ---------------------------------------------------------------------------
// Chart data builder — esportato per i test
// ---------------------------------------------------------------------------

export interface FunnelEntry {
  stage: string
  total: number
  weighted: number | null
  deal_count: number
  aging_median_days: number | null
  aged_count: number
  aged: boolean  // aging > threshold
}

export function buildFunnelData(stages: PipelineStage[], threshold: number): FunnelEntry[] {
  return stages.map(s => ({
    stage: s.stage,
    total: parseFloat(s.total_value),
    weighted: s.weighted_value != null ? parseFloat(s.weighted_value) : null,
    deal_count: s.deal_count,
    aging_median_days: s.aging_median_days,
    aged_count: s.aged_count,
    aged: isAged(s, threshold),
  }))
}

// ---------------------------------------------------------------------------
// Custom Y-axis tick con aging warning
// ---------------------------------------------------------------------------

interface AgingTickProps {
  x?: number
  y?: number
  payload?: { value: string }
  aged: boolean
}

function AgingTick({ x = 0, y = 0, payload, aged }: AgingTickProps) {
  const label = payload?.value ?? ''
  return (
    <g transform={`translate(${x},${y})`}>
      <text
        x={-4}
        y={0}
        dy={4}
        textAnchor="end"
        fontSize={11}
        fill="var(--km-text-secondary)"
      >
        {label}
      </text>
      {aged && (
        <text x={-4 - label.length * 6.5} y={0} dy={4} fontSize={11} fill="#f59e0b">
          ⚠
        </text>
      )}
    </g>
  )
}

// ---------------------------------------------------------------------------
// Custom Tooltip
// ---------------------------------------------------------------------------

interface TooltipProps {
  active?: boolean
  payload?: Array<{ payload: FunnelEntry; dataKey: string; value: number }>
  agedThreshold: number
}

function FunnelTooltip({ active, payload, agedThreshold }: TooltipProps) {
  if (!active || !payload?.length) return null
  const entry = payload[0].payload

  return (
    <div className="rounded-xl border border-[var(--km-card-border)] bg-[var(--km-card-bg)] px-3 py-2.5 text-xs shadow-lg min-w-[180px]">
      <p className="font-semibold text-[--km-text-primary] mb-2 capitalize">{entry.stage}</p>
      <div className="space-y-1 text-[--km-text-secondary]">
        <p>Valore totale: <span className="text-[--km-text-primary]">{formatKpiEur(entry.total)}</span></p>
        {entry.weighted != null && (
          <p>Weighted: <span className="text-[--km-text-primary]">{formatKpiEur(entry.weighted)}</span></p>
        )}
        <p>Deal: <span className="text-[--km-text-primary]">{entry.deal_count}</span></p>
        <p>Aging mediano: <span className={entry.aged ? 'text-amber-400' : 'text-[--km-text-primary]'}>{fmtDays(entry.aging_median_days)}</span></p>
        {entry.aged_count > 0 && (
          <p className="text-amber-400">{entry.aged_count} deal &gt;{agedThreshold}gg</p>
        )}
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// PipelineFunnelChart
// ---------------------------------------------------------------------------

interface PipelineFunnelChartProps {
  pipeline: BrPipeline
}

export function PipelineFunnelChart({ pipeline }: PipelineFunnelChartProps) {
  const { pipeline_by_stage, aged_threshold_days, aged_deals_count, aged_deals_value } = pipeline

  if (!pipeline_by_stage.length) {
    return (
      <div
        data-testid="funnel-empty"
        className="flex items-center justify-center h-32 rounded-xl border border-dashed border-[var(--km-card-border)] text-xs text-[--km-text-secondary]"
      >
        Nessun dato pipeline per il periodo selezionato
      </div>
    )
  }

  const data = buildFunnelData(pipeline_by_stage, aged_threshold_days)
  const showWeighted = hasWeighted(pipeline_by_stage)
  const chartHeight = Math.max(160, data.length * 52)

  return (
    <div data-testid="funnel-chart" className="mt-4 space-y-3">
      {/* Sommario deal stagnanti */}
      {aged_deals_count != null && aged_deals_count > 0 && (
        <div
          data-testid="funnel-stagnant-summary"
          className="flex items-center gap-2 rounded-xl border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs text-amber-300"
        >
          <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
          <span>
            <strong>{aged_deals_count} deal stagnanti</strong> per {formatKpiEur(parseFloat(String(aged_deals_value ?? 0)))} — oltre {aged_threshold_days}gg senza progressione
          </span>
        </div>
      )}

      {/* Nota se probability non disponibile */}
      {!showWeighted && (
        <p
          data-testid="funnel-no-probability"
          className="text-[10px] text-[--km-text-secondary] italic"
        >
          Valore pesato non disponibile — configura la probabilità di chiusura nel mapping ordini
        </p>
      )}

      <ResponsiveContainer width="100%" height={chartHeight}>
        <BarChart
          data={data}
          layout="vertical"
          margin={{ top: 0, right: 16, left: 8, bottom: 0 }}
        >
          <XAxis
            type="number"
            tickFormatter={(v: number) => `€${(v / 1000).toFixed(0)}k`}
            tick={{ fontSize: 10, fill: 'var(--km-text-secondary)' }}
            axisLine={false}
            tickLine={false}
          />
          <YAxis
            type="category"
            dataKey="stage"
            width={96}
            tick={(props) => (
              <AgingTick
                {...props}
                aged={data.find(d => d.stage === props.payload?.value)?.aged ?? false}
              />
            )}
          />
          <Tooltip
            content={<FunnelTooltip agedThreshold={aged_threshold_days} />}
            cursor={{ fill: 'rgba(255,255,255,0.04)' }}
          />

          {/* Barra total_value — indigo chiaro */}
          <Bar dataKey="total" name="Valore totale" radius={[0, 3, 3, 0]} barSize={14} isAnimationActive={false}>
            {data.map((entry, i) => (
              <Cell key={`total-${i}`} fill={entry.aged ? '#f59e0b' : '#6366f1'} fillOpacity={0.5} />
            ))}
          </Bar>

          {/* Barra weighted_value — indigo pieno (se probability disponibile) */}
          {showWeighted && (
            <Bar dataKey="weighted" name="Weighted" radius={[0, 3, 3, 0]} barSize={8} isAnimationActive={false}>
              {data.map((entry, i) => (
                <Cell key={`w-${i}`} fill={entry.aged ? '#f59e0b' : '#6366f1'} />
              ))}
            </Bar>
          )}
        </BarChart>
      </ResponsiveContainer>

      {/* Legenda */}
      <div className="flex items-center gap-4 justify-center text-[10px] text-[--km-text-secondary]">
        <span className="flex items-center gap-1">
          <span className="w-3 h-2 rounded-sm bg-indigo-500 opacity-50" />
          Valore totale
        </span>
        {showWeighted && (
          <span className="flex items-center gap-1">
            <span className="w-3 h-2 rounded-sm bg-indigo-500" />
            Weighted
          </span>
        )}
        <span className="flex items-center gap-1">
          <span className="w-3 h-2 rounded-sm bg-amber-400" />
          Aged &gt;{aged_threshold_days}gg
        </span>
      </div>
    </div>
  )
}
